/**
 * Same-page anchor resolution for editor-supplied targets.
 *
 * An anchor may be either:
 *  - an element id — `lead-magnet-form` (LeadMagnetWithForm hardcodes this one), or
 *  - a slice type — `cta_with_side_form`. Every slice root renders
 *    `data-slice-type`, so this works against slices that expose no `id`,
 *    which is most of the library.
 *
 * Generalizes the hardcoded `document.querySelector('[data-slice-type="…"]')`
 * lookups in CenteredCta, CalloutAction, ParagraphBannerCta and AboutSection.
 *
 * NOTE: deliberately not re-exported from `@/utils` — that barrel pulls in
 * `cached-settings` / `cached-pages`, which are server-only. Import this
 * module directly so client components don't drag them into the bundle.
 */

function escapeSelectorValue(value: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value;
}

/** Element id first, then the slice-type fallback. */
function findAnchorTarget(anchor: string): Element | null {
  return (
    document.getElementById(anchor) ??
    document.querySelector(`[data-slice-type="${escapeSelectorValue(anchor)}"]`)
  );
}

/**
 * Scrolls to the element the anchor resolves to.
 *
 * Returns `true` when a target was found and scrolled to. Callers use the
 * return value to decide whether to `preventDefault()` on a plain `#hash` link,
 * so a normal id-based anchor keeps its native browser behaviour.
 */
export function scrollToAnchor(
  anchor: string | null | undefined,
  { smooth = true }: { smooth?: boolean } = {},
): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const normalized = normalizeAnchor(anchor);
  if (!normalized) {
    return false;
  }

  const target = findAnchorTarget(normalized);
  if (!target) {
    return false;
  }

  target.scrollIntoView({
    behavior: smooth ? 'smooth' : 'auto',
    block: 'start',
  });
  return true;
}

/**
 * The single normalization rule for anchors, so callers never reimplement it.
 * Prismic editors may or may not type the leading `#`; both forms are accepted.
 * Returns `undefined` for anything that isn't a usable target.
 */
export function normalizeAnchor(
  raw: string | null | undefined,
): string | undefined {
  return raw?.trim().replace(/^#+/, '') || undefined;
}

/**
 * Whether a raw value yields a usable anchor, for callers that only need the
 * yes/no answer (e.g. choosing which element to render) and shouldn't have to
 * hold on to the normalized string themselves. Narrows the type so the raw
 * value can be handed straight to whoever does own the normalization.
 */
export function hasAnchor(raw: string | null | undefined): raw is string {
  return normalizeAnchor(raw) !== undefined;
}

/**
 * A slice type could in principle contain selector-significant characters, so
 * escape before interpolating into a query. The fallback keeps older engines
 * (and any non-DOM environment) safe.
 */
